import Api from '.';
import { endpoints } from './endpoints';
import { setSessionId, getUserId, notifyJourney } from './journey';

/**
 * Stage 0 (Arrival) and journey-wide endpoints.
 *
 * /journey/init is anonymous and seeds the X-Session-Id GUID that every
 * subsequent call needs.
 */
export default class JourneyApi extends Api {
    /**
     * GET /journey/init
     *
     * Caller passes the optional UTM/device-context object; serialised as
     * query params. On success, the returned sessionId is persisted into
     * sessionStorage + redux so all later calls stamp X-Session-Id.
     */
    async init(params = {}) {
        const qs = Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
            .join('&');

        const url = this.buildUrl(endpoints.stages.journey.init + (qs ? `?${qs}` : ''));
        const data = await this.fetch(url, 'GET', undefined, { anonymous: true });
        if (data && data.sessionId) setSessionId(data.sessionId);
        return data;
    }

    /**
     * GET /journey/status?userId={long}  (JWT required)
     *
     * `userId` MUST be the numeric long from the JWT `sub` claim, NOT the
     * leadId Guid. Wrapper defaults to `getUserId()` so callers don't have
     * to know the difference; explicit override (`status(id)`) still works.
     *
     * Per TKT-0001 (2026-05-14), `data` now carries:
     *   currentStage    : number
     *   currentState    : string  (e.g. AOF_GENERATED, NON_STP_REVIEW, DROPPED)
     *   stpDecision     : "STP" | "NON_STP" | null
     *   stpReasonCodes  : string[]  (lowercase stp_*_flag tokens, [] when none)
     *   dropCode        : string | null
     *   esignMismatchError : string | null (hard eSign-vs-Aadhaar mismatch reason,
     *     only set when currentState=DETAILS_DONE after a hard mismatch)
     *   isCompleted     : bool, progressPercent: number, createdAt/updatedAt
     * envelope.journey.* still mirrors currentStage/currentState for the
     * cross-call JOURNEY_UPDATED side-effect.
     */
    status(userId) {
        const id = userId != null && userId !== '' ? userId : getUserId();
        const qs = id ? `?userId=${encodeURIComponent(id)}` : '';
        return this.fetch(this.buildUrl(endpoints.stages.journey.status + qs), 'GET').then((data) => {
            // Shape-bridge: /journey/status returns the journey fields under
            // `data` and leaves envelope.journey = null, so unwrapEnvelope
            // can't dispatch JOURNEY_UPDATED for this endpoint. Synthesize
            // the journey block from data and dispatch it here so the redux
            // journey reducer is populated on /dad/* refreshes (otherwise
            // routeForJourney falls to its catch-all and bounces to verifyEmail).
            if (data && (data.currentStage !== undefined || data.currentState)) {
                notifyJourney({
                    currentStage: data.currentStage,
                    currentState: data.currentState,
                    progressPercent: data.progressPercent,
                    isCompleted: data.isCompleted,
                    dropCode: data.dropCode,
                    journeyPath: data.journeyPath,
                    // Rejection Correction session block (spec §8.3) — drives
                    // resume routing back to the correction page after eSign
                    // fail/abandon while a correction session is active.
                    correction: data.correction,
                    esignMismatchError: data.esignMismatchError,
                    // Assisted-journey signal (2026-09): raw leads.flow_type plus the BE's
                    // isAssisted = flow_type ASSISTED (?ekycid= / ?zid= entry) || RM
                    // created/resumed the lead (is_rm_resume_bypass). Read by the Stage 8
                    // selfie screen to skip the camera auto-launch. Persisted BE-side, so
                    // unlike user.isBRBALogin it survives a refresh.
                    flowType: data.flowType,
                    isAssisted: data.isAssisted,
                    // Alliance signal (BE bbd92b62): `isAlliance` is true for any lead a
                    // partner bank originated (channel "ALLIANCE"); `alliance` is which
                    // bank (e.g. "IDFC"). Persisted BE-side, so unlike user.handoffAlliance
                    // it survives a refresh and a later session. Read by Stage 3 to decide
                    // whether the bank-supplied email can go straight to OTP. Never branch
                    // on the partner code itself — it is for display/diagnostics only.
                    isAlliance: data.isAlliance,
                    alliance: data.alliance,
                    // Cross-sell layer signal: the backend sends it in envelope.journey only
                    // (JourneyStatus.CrossSell), and unwrapEnvelope has already stored it as
                    // authoritative. Re-dispatch it here only if `data` ever carries the key,
                    // otherwise this bridge would overwrite a PENDING layer with null (DEV lead
                    // 22868682 went to Congratulations instead of F&O → SSP).
                    ...(data.crossSell !== undefined ? { crossSell: data.crossSell } : {}),
                }, data.crossSell !== undefined ? { crossSellAuthoritative: true } : undefined);
            }
            return data;
        });
    }

    /**
     * POST /app/version-check  (anonymous)
     *
     * Body: { appVersion, platform }. Response: { isSupported, minVersion, currentVersion }.
     * FE derives forceUpgrade = !isSupported until BE ticket BE-S0-V1 lands.
     */
    versionCheck({ appVersion, platform } = {}) {
        const payload = {
            appVersion: appVersion || process.env.REACT_APP_VERSION || '1.0.0',
            platform: platform || 'WEB',
        };
        return this.fetch(
            this.buildUrl(endpoints.stages.app.versionCheck),
            'POST',
            JSON.stringify(payload),
            { anonymous: true },
        );
    }

    /** POST /journey/stage  (analytics breadcrumb) */
    recordStage(payload) {
        return this.fetch(
            this.buildUrl(endpoints.stages.journey.stage),
            'POST',
            JSON.stringify(payload),
            { lead: { mode: 'body' } },
        );
    }

    /**
     * POST /journey/final-validation  (Stage 11).
     *
     * Per TKT-0054 (closed 2026-05-12), the seven-check rollup is **not**
     * auto-triggered. FE must call this explicitly after Stage 9
     * `PUT /personal-details` (current_state=DETAILS_DONE) — and after
     * Stage 10 `/income-proof/upload` in the F&O lane, in flows that still
     * route through a standalone income-proof page.
     *
     * Response envelope is `ApiResponse<FinalValidationResult>`:
     *   data.stpDecision    : "STP" | "NON_STP"
     *   data.stpReasonCodes : string[]  (lowercase `stp_*_flag` tokens —
     *     stp_aadhaar_flag, stp_bank_flag, stp_face_flag, stp_income_flag,
     *     stp_signature_flag, stp_pep_flag, stp_csafe_flag,
     *     stp_esign_mismatch_flag, stp_aml_flag, stp_field_validation_flag)
     *   journey.currentState becomes "FINAL_VALIDATION" on success.
     *   Hard-stop drops surface on 409 as journey.dropCode + errors[0].code
     *   (DropCodes.FinalPan/FinalPanChanged/FinalNegativeList/FinalDedupe/CsAofFail).
     */
    finalValidation() {
        return this.fetch(
            this.buildUrl(endpoints.stages.journey.finalValidation),
            'POST',
            JSON.stringify({}),
            { lead: { mode: 'body' } },
        );
    }
}
